import type { FunctionComponent } from 'react';
import React, { useCallback } from 'react';
import classNames from 'classnames';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { isEmbeddedInAtlassian } from '@trello/browser';
import { forTemplate } from '@trello/i18n';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';
import { BoardHeaderTestIds } from '@trello/test-ids';

import { LazyBoardInviteModal } from 'app/src/components/BoardInviteModal';
import { useCurrentBoardBackground } from 'app/src/components/BoardTheme';

import styles from './BoardShareButton.less';

const format = forTemplate('board_share_button');

interface BoardShareButtonProps {
  idBoard: string;
  idOrg: string;
}

export const BoardShareButton: FunctionComponent<BoardShareButtonProps> = ({
  idBoard,
  idOrg,
}) => {
  const {
    show: showDialog,
    hide: hideDialog,
    isOpen: isDialogOpen,
    dialogProps,
  } = useDialog();

  const { backgroundBrightness } = useCurrentBoardBackground();
  const isLightTheme = backgroundBrightness === 'light';
  const isEmbedded = isEmbeddedInAtlassian();

  const openBoardShareModal = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'shareButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization: idOrg,
        idWorkspace: idOrg,
      }),
    });

    showDialog();
  }, [idBoard, idOrg, showDialog]);

  return (
    <>
      <button
        title={format('share-board')}
        className={classNames(
          styles.boardShareButton,
          isLightTheme && styles.lightBackground,
          isEmbedded && styles.embedded,
        )}
        data-testid={BoardHeaderTestIds.BoardShareButton}
        onClick={openBoardShareModal}
      >
        {!isEmbedded && (
          <AddMemberIcon
            testId={BoardHeaderTestIds.BoardShareButtonIcon}
            color="currentColor"
            dangerous_className={styles.boardShareButtonIcon} // needed for print color styles
            size="small"
          />
        )}
        <span className={styles.boardShareButtonText}>{format('share')}</span>
      </button>
      {isDialogOpen && (
        <Dialog
          alignment="center"
          className={styles.inviteDialog}
          {...dialogProps}
        >
          <LazyBoardInviteModal
            idBoard={idBoard}
            idOrg={idOrg}
            onClose={hideDialog}
          />
        </Dialog>
      )}
    </>
  );
};
